/**
 * Reader (method rule 3: the text comes first, help only on request).
 *  - Whole text: read once for the gist.
 *  - One part (`sentenceIds`): the same, sentence by sentence, with a «Перевод» button per
 *    sentence, key sentences highlighted and grammar notes folded under their sentences.
 * Neither shows pinyin or a translation by default: tapping a word opens a small card
 * just above it (pinyin, meaning, sound, «+ В карточки»). The layout never moves on its
 * own — no auto-scroll, and the card floats over the text instead of pushing it.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GrammarPoint, Lesson, LessonText, Sentence, Token } from '../../core/content.ts';
import { findSentence, findWord, langCode } from '../../core/content.ts';
import { tokensFor } from '../../core/segment.ts';
import { useAppData, useLoadedData } from '../../state/app-data.tsx';
import { useTutor } from '../../state/tutor.tsx';
import { routes, useRouter } from '../../state/router.tsx';
import { TABLET_UP, useMediaQuery } from '../../state/use-media.ts';
import { ChatPanel } from '../../ui/ChatPanel.tsx';
import { Icon } from '../../ui/Icon.tsx';
import { speak } from '../../ui/speech.ts';
import { WordCard, type WordInfo } from './WordCard.tsx';

interface Selection {
  token: Token;
  sentence: Sentence;
  /** Popover anchor inside the text column, in px. */
  x: number;
  y: number;
  below: boolean;
}

/** Room the popover needs above a word; otherwise it opens below. */
const POPOVER_ROOM = 190;

function wordInfo(lesson: Lesson, token: Token, sentence: Sentence): WordInfo {
  const word = token.wordId ? findWord(lesson, token.wordId) : undefined;
  const exampleSentence =
    (word?.exampleSentenceId ? findSentence(lesson, word.exampleSentenceId) : undefined) ?? sentence;
  const pinyin = token.pinyin ?? word?.pinyin;
  const ru = token.ru ?? word?.ru;
  return {
    surface: word?.word ?? token.surface,
    ...(pinyin ? { pinyin } : {}),
    ...(ru ? { ru } : {}),
    ...(word?.pos ? { pos: word.pos } : {}),
    ...(word?.hskLevel ? { hskLevel: word.hskLevel } : {}),
    example: { text: exampleSentence.text, ru: exampleSentence.ru },
  };
}

/** Grammar points the sentence illustrates: tagged tokens or a book example taken from it. */
function grammarFor(lesson: Lesson, sentence: Sentence): GrammarPoint[] {
  const tagged = new Set(sentence.tokens.map((t) => t.grammarId).filter(Boolean));
  return lesson.grammar.filter(
    (g) => tagged.has(g.id) || g.examples.some((e) => e.fromTextSentenceId === sentence.id),
  );
}

function cardRef(lang: string, token: Token): string {
  return token.wordId ?? `${lang}:${token.surface.toLowerCase()}`;
}

/** Punctuation stays on the same line as the word before it. */
function groupTokens(tokens: readonly Token[]): number[][] {
  const groups: number[][] = [];
  tokens.forEach((token, i) => {
    const attaches = token.punct && !/\s/.test(token.surface) && groups.length > 0;
    if (attaches) groups[groups.length - 1]!.push(i);
    else groups.push([i]);
  });
  return groups;
}

type TapHandler = (sentence: Sentence, token: Token, target: HTMLElement) => void;

/** One sentence's words; memoised so a tap re-renders only the sentences whose highlight changes. */
const SentenceText = memo(function SentenceText({
  sentence,
  tokens,
  selectedIndex,
  onTap,
}: {
  sentence: Sentence;
  tokens: readonly Token[];
  selectedIndex: number;
  onTap: TapHandler;
}) {
  const renderToken = (token: Token, i: number) =>
    token.punct ? (
      <span key={i}>{token.surface}</span>
    ) : (
      <button
        key={i}
        type="button"
        className={`token${token.wordId ? ' token--word' : ''}${i === selectedIndex ? ' token--selected' : ''}`}
        onClick={(e) => onTap(sentence, token, e.currentTarget)}
      >
        {token.surface}
      </button>
    );
  return (
    <>
      {groupTokens(tokens).map((group) =>
        group.length === 1 ? (
          renderToken(tokens[group[0]!]!, group[0]!)
        ) : (
          <span key={group[0]} className="token-group">
            {group.map((i) => renderToken(tokens[i]!, i))}
          </span>
        ),
      )}
    </>
  );
});

export function Reader({
  lesson,
  text,
  sentenceIds,
  speechLang,
  onDone,
}: {
  lesson: Lesson;
  text: LessonText;
  /** One part of the text; without it the whole text is shown for a first read. */
  sentenceIds?: readonly string[];
  speechLang: string;
  onDone: () => void;
}) {
  const wide = useMediaQuery(TABLET_UP);
  const { navigate } = useRouter();
  const { cards } = useLoadedData();
  const { addCards } = useAppData();
  const { setDraft, setSentence } = useTutor();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [translated, setTranslated] = useState<ReadonlySet<string>>(() => new Set());
  const columnRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const lang = langCode(lesson.targetLang);
  const partMode = sentenceIds !== undefined;

  const tokenized = useMemo(() => {
    const visible = partMode ? text.sentences.filter((s) => sentenceIds.includes(s.id)) : text.sentences;
    return visible.map((s) => ({ sentence: s, tokens: tokensFor(s, lesson.words), grammar: grammarFor(lesson, s) }));
  }, [text, lesson, partMode, sentenceIds]);
  const firstNumber = partMode && tokenized[0] ? text.sentences.indexOf(tokenized[0].sentence) + 1 : 1;

  // The tutor knows which part is being read.
  useEffect(() => {
    if (!partMode || tokenized.length === 0) return;
    setSentence({
      text: tokenized.map((t) => t.sentence.text).join(' '),
      ru: tokenized.map((t) => t.sentence.ru).join(' '),
    });
    return () => setSentence(null);
  }, [partMode, tokenized, setSentence]);

  // Close the word card on a tap outside it or on Escape.
  useEffect(() => {
    if (!selection) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('.token')) return;
      setSelection(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSelection(null);
    document.addEventListener('pointerdown', onPointer, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [selection]);

  const tap = useCallback<TapHandler>(
    (sentence, token, target) => {
      const column = columnRef.current;
      if (!column) return;
      const box = column.getBoundingClientRect();
      const word = target.getBoundingClientRect();
      const below = word.top < POPOVER_ROOM;
      setSelection({
        sentence,
        token,
        x: word.left + word.width / 2 - box.left,
        y: (below ? word.bottom : word.top) - box.top,
        below,
      });
      speak(token.surface, speechLang);
    },
    [speechLang],
  );

  const existingRefs = useMemo(() => new Set(cards.filter((c) => c.kind === 'word').map((c) => c.ref_id)), [cards]);
  const info = selection ? wordInfo(lesson, selection.token, selection.sentence) : null;

  const addSelection = () => {
    if (!selection || !info) return;
    addCards([
      {
        kind: 'word',
        ref_id: cardRef(lang, selection.token),
        lang,
        front: {
          text: info.surface,
          ...(info.pinyin ? { pinyin: info.pinyin } : {}),
          ...(info.example ? { sentence: info.example.text } : {}),
        },
        back: {
          ru: info.ru ?? '',
          ...(info.pos ? { pos: info.pos } : {}),
          ...(info.example ? { sentenceRu: info.example.ru } : {}),
        },
      },
    ]);
  };

  const toggleTranslation = (id: string) =>
    setTranslated((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const askTeacher = () => {
    setDraft(`Объясни этот отрывок: ${tokenized.map((t) => t.sentence.text).join(' ')}`);
    if (!wide) navigate(routes.chat);
  };

  // Clamp the card inside the column; its width is set in CSS (min(320px, 100% - 16px)).
  const columnWidth = columnRef.current?.clientWidth ?? 0;
  const cardWidth = Math.min(320, columnWidth - 16);
  const left = selection ? Math.min(Math.max(selection.x - cardWidth / 2, 8), Math.max(8, columnWidth - cardWidth - 8)) : 0;

  return (
    <div className={`reader${wide && partMode ? ' reader--wide' : ''}`}>
      <div className="reader__text" ref={columnRef}>
        <p className="t-caps">{partMode ? 'Разбираем по частям' : 'Читаем целиком'}</p>
        <p className="reader__hint">
          {partMode
            ? 'Читайте сами. Непонятное слово — нажмите на него; перевод предложения — кнопка «Перевод».'
            : 'Прочитайте весь текст и поймите общий смысл. Незнакомое слово можно нажать.'}
        </p>
        {text.title && !partMode && <h2 className="reader__title han">{text.title}</h2>}

        <ol className={`reader__sentences${partMode ? '' : ' reader__sentences--plain'}`} lang={speechLang} start={firstNumber}>
          {tokenized.map(({ sentence, tokens, grammar }, index) => {
            const key = (sentence.newWordIds?.length ?? 0) >= 2 || grammar.length > 0;
            const selectedIndex =
              selection?.sentence.id === sentence.id ? tokens.indexOf(selection.token) : -1;
            return (
              <li key={sentence.id} className={`sentence${partMode && key ? ' sentence--key' : ''}`}>
                {partMode && <span className="sentence__number">{firstNumber + index}</span>}
                <div className="sentence__body">
                  {sentence.speaker && <span className="sentence__speaker">{sentence.speaker}: </span>}
                  <span className="sentence__text">
                    <SentenceText sentence={sentence} tokens={tokens} selectedIndex={selectedIndex} onTap={tap} />
                  </span>
                  {partMode && (
                    <div className="sentence__tools">
                      <button
                        type="button"
                        className={`chip-btn${translated.has(sentence.id) ? ' chip-btn--on' : ''}`}
                        onClick={() => toggleTranslation(sentence.id)}
                        aria-expanded={translated.has(sentence.id)}
                      >
                        Перевод
                      </button>
                      <button
                        type="button"
                        className="chip-btn"
                        onClick={() => speak(sentence.text, speechLang)}
                        aria-label="Прослушать предложение"
                      >
                        <Icon name="speaker" size={16} />
                      </button>
                    </div>
                  )}
                  {translated.has(sentence.id) && (
                    <p className="sentence__ru" lang="ru">
                      {sentence.ru}
                    </p>
                  )}
                  {partMode &&
                    grammar.map((g) => (
                      <details key={g.id} className="grammar-note">
                        <summary>
                          <span className="grammar-note__tag">Грамматика</span>
                          <strong className="grammar-note__title han">{g.title}</strong>
                        </summary>
                        {g.pattern && <span className="grammar-note__pattern han">{g.pattern}</span>}
                        <p className="grammar-note__text" lang="ru">
                          {g.explanation_ru}
                        </p>
                      </details>
                    ))}
                </div>
              </li>
            );
          })}
        </ol>

        {selection && info && (
          <div
            ref={popoverRef}
            className={`word-popover${selection.below ? ' word-popover--below' : ''}`}
            style={{ left, top: selection.y, width: cardWidth }}
            role="dialog"
            aria-label={`Слово ${info.surface}`}
          >
            <WordCard
              word={info}
              speechLang={speechLang}
              added={existingRefs.has(cardRef(lang, selection.token))}
              onAdd={addSelection}
              compact
            />
          </div>
        )}

        <div className="lesson__footer reader__footer">
          {partMode && (
            <button type="button" className="btn btn--text" onClick={askTeacher}>
              <Icon name="chat" size={18} /> Спросить учителя
            </button>
          )}
          <button type="button" className="btn btn--primary" onClick={onDone}>
            Продолжить
          </button>
        </div>
      </div>

      {wide && partMode && (
        <aside className="reader__panel">
          <ChatPanel compact subtitle="читает этот отрывок вместе с вами" />
        </aside>
      )}
    </div>
  );
}
